import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { useSubscription } from '@/hooks/useSubscription';
import { AlertTriangle, TrendingUp, Zap } from 'lucide-react';

interface UsageLimitAlertProps {
  type: 'customers' | 'stamps' | 'referrals';
  currentUsage: number;
  showUpgrade?: boolean;
}

export function UsageLimitAlert({ type, currentUsage, showUpgrade = true }: UsageLimitAlertProps) {
  const { currentFeatures, getRemainingCustomers, getRemainingStamps, getRemainingReferrals } = useSubscriptionAccess();
  const { createCheckout } = useSubscription();

  const getUsageData = () => {
    switch (type) {
      case 'customers':
        return {
          limit: currentFeatures.maxCustomers,
          remaining: getRemainingCustomers(currentUsage),
          usage: currentUsage,
          label: 'Kunden',
          icon: '👥'
        };
      case 'stamps':
        return {
          limit: currentFeatures.maxStampsPerMonth,
          remaining: getRemainingStamps(currentUsage),
          usage: currentUsage,
          label: 'Stempel (diesen Monat)',
          icon: '🏷️'
        };
      case 'referrals':
        return {
          limit: currentFeatures.maxReferralsPerMonth,
          remaining: getRemainingReferrals(currentUsage),
          usage: currentUsage,
          label: 'Empfehlungen (diesen Monat)',
          icon: '🤝'
        };
    }
  };

  const data = getUsageData();
  const usagePercentage = data.limit === Infinity ? 0 : (data.usage / data.limit) * 100;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = data.remaining <= 0;

  // Don't show alert if usage is low and user has unlimited
  if (data.limit === Infinity || usagePercentage < 80) {
    return null;
  }

  const handleUpgrade = (plan: string) => {
    createCheckout(plan);
  };

  return (
    <Alert className={`${isAtLimit ? 'border-destructive bg-destructive/5' : 'border-yellow-500 bg-yellow-50'}`}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle className="flex items-center gap-2">
        {data.icon} {isAtLimit ? 'Limit erreicht' : 'Limit fast erreicht'}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">
              {data.label}: {data.usage} von {data.limit}
            </span>
            <span className="text-sm text-muted-foreground">
              {data.remaining} verbleibend
            </span>
          </div>
          <Progress value={usagePercentage} className="h-2" />
        </div>
        
        {isAtLimit && (
          <p className="text-sm text-destructive font-medium">
            Sie haben Ihr monatliches Limit erreicht. Upgraden Sie für mehr Kapazität.
          </p>
        )}
        
        {showUpgrade && (
          <div className="flex gap-2 pt-2">
            <Button 
              size="sm" 
              onClick={() => handleUpgrade('pro')}
              className="flex items-center gap-1"
            >
              <Zap className="h-3 w-3" />
              Pro Plan
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleUpgrade('enterprise')}
              className="flex items-center gap-1"
            >
              <TrendingUp className="h-3 w-3" />
              Enterprise
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}